import { z } from 'zod';

/**
 * A TR-069 deployment an ONU can be pointed at. `local` is SimpleACS's own
 * GenieACS for the org (superadmin-managed, one per org); `external` is another
 * operator's ACS an org admin adds. The password is never returned.
 */
/**
 * Where a profile's ACS URL comes from. WireGuard/OpenVPN derive it from that
 * tunnel's ACS gateway (safer than free-typing — a CWMP typo takes ONUs
 * offline); `custom` keeps a manually-entered URL (always the case for external).
 */
export const cwmpTunnelSchema = z.enum(['wireguard', 'openvpn', 'custom']);
export type CwmpTunnel = z.infer<typeof cwmpTunnelSchema>;

export const tr069ProfileSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  kind: z.enum(['local', 'external']),
  acsUrl: z.string(),
  cwmpTunnel: cwmpTunnelSchema,
  informUsername: z.string().nullable(),
  /** True when an inform password is stored (the value itself is never sent). */
  hasInformPassword: z.boolean(),
  priority: z.number().int(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Tr069Profile = z.infer<typeof tr069ProfileSchema>;

export const createTr069ProfileSchema = z.object({
  name: z.string().min(1).max(64),
  /** Only a superadmin may create/edit a `local` profile. */
  kind: z.enum(['local', 'external']).default('external'),
  /** Required for `custom`; ignored (derived) when a tunnel is chosen. */
  acsUrl: z.string().url().optional(),
  cwmpTunnel: cwmpTunnelSchema.default('custom'),
  informUsername: z.string().max(64).nullable().optional(),
  informPassword: z.string().max(128).nullable().optional(),
  priority: z.number().int().min(0).max(7).default(2),
});
export type CreateTr069Profile = z.infer<typeof createTr069ProfileSchema>;

export const updateTr069ProfileSchema = createTr069ProfileSchema.partial().omit({ kind: true });
export type UpdateTr069Profile = z.infer<typeof updateTr069ProfileSchema>;

/** The candidate CWMP URLs a profile can derive from each tunnel, for the picker. */
export const cwmpOptionsSchema = z.object({
  wireguard: z.string(),
  openvpn: z.string(),
});
export type CwmpOptions = z.infer<typeof cwmpOptionsSchema>;

export const tr069ProfilesResponseSchema = z.object({
  items: z.array(tr069ProfileSchema),
  cwmpOptions: cwmpOptionsSchema,
});
export type Tr069ProfilesResponse = z.infer<typeof tr069ProfilesResponseSchema>;

/** One TR-069/GenieACS service (CWMP, NBI, File, UI) and whether it answered. */
export const tr069ServiceHealthSchema = z.object({
  name: z.string(),
  url: z.string(),
  ok: z.boolean(),
  latencyMs: z.number().nullable(),
});
export type Tr069ServiceHealth = z.infer<typeof tr069ServiceHealthSchema>;

export const tr069ProfileHealthSchema = z.object({
  profileId: z.string(),
  name: z.string(),
  kind: z.enum(['local', 'external']),
  /** Overall: the CWMP endpoint (what ONUs inform to) is reachable. */
  reachable: z.boolean(),
  services: z.array(tr069ServiceHealthSchema),
});
export type Tr069ProfileHealth = z.infer<typeof tr069ProfileHealthSchema>;

/** An OLT's associated profiles, with which one new ONUs default to. */
export const oltTr069ProfileSchema = z.object({
  profileId: z.string(),
  name: z.string(),
  kind: z.enum(['local', 'external']),
  isDefault: z.boolean(),
});
export type OltTr069Profile = z.infer<typeof oltTr069ProfileSchema>;

export const setOltTr069ProfilesSchema = z.object({
  /** The profiles this OLT may use. Empty falls back to the org's local profile. */
  profileIds: z.array(z.string()).max(16),
  /** Which of them new ONUs inherit; must be in profileIds. */
  defaultProfileId: z.string().nullable().optional(),
});
export type SetOltTr069Profiles = z.infer<typeof setOltTr069ProfilesSchema>;
